<script lang="ts" module>
	import lastImageXs from "@assets/images/fathers-family-club-last.jpg?webp&h=1000&imagetools";
	import lastImageSm from "@assets/images/fathers-family-club-last.jpg?webp&h=1300&imagetools";
	import lastImageLg from "@assets/images/fathers-family-club-last.jpg?webp&h=1600&imagetools";
	import lastImage3xl from "@assets/images/fathers-family-club-last.jpg?webp&imagetools";
	import clubLogo from "@assets/images/fathers-family-club.png?webp&w=600&imagetools";
	import BuildYourSubscriptionButton from "@components/BuildYourSubscriptionButton.svelte";
	import FloatingCta from "@components/FloatingCta.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Media from "@components/common/Media.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import FathersChoiceSection from "@components/sections/FathersChoiceSection.svelte";
	import FathersClubAdvantagesSection from "@components/sections/FathersClubAdvantagesSection.svelte";
	import FathersClubFaqSection from "@components/sections/FathersClubFaqSection.svelte";
	import FathersClubHeroSection from "@components/sections/FathersClubHeroSection.svelte";
	import FathersClubHowItWorksSection from "@components/sections/FathersClubHowItWorksSection.svelte";
	import FathersClubPricingSection from "@components/sections/FathersClubPricingSection.svelte";
	import ImageSection from "@components/sections/ImageSection.svelte";
	import ProductSection from "@components/sections/ProductSection.svelte";
	import SubscriptionKindsSection from "@components/sections/SubscriptionKindsSection.svelte";
	import SubscriptionSection from "@components/sections/SubscriptionSection.svelte";
	import TaglineStrip from "@components/sections/TaglineStrip.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { calculatePriceRangeOfRecurringSubscription } from "@core/schema/utils/calculatePriceRangeOfRecurringSubscription.js";
	import { formatPrice, formatPriceRange } from "@core/utils/formatPrice.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { defaultDestinationCountry } from "../defaultDestinationCountry.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import translations from "./FathersClub.translations.json" with { type: "json" };
	import type { FathersClubProps } from "./FathersClubProps.js";

	const lastImageHeight = tw("!h-[max(calc(100vh-8rem),32rem)]");
</script>

<script lang="ts">
	const translate = getTranslate(translations);
	const {
		tenant,
		subcategories,
		products,
		mothersBlendProduct,
		cafeinaProduct,
		exchangeRatesSetting,
		countrySettings,
	}: FathersClubProps = $props();
	const { MOTHERS_BLEND_CONSTANT_PRICE_ENABLED } = getGlobalSettings();

	const currency = $derived(selectedCurrency.current ?? tenant.defaultCurrency);
	const prices = $derived(
		calculatePriceRangeOfRecurringSubscription({
			subcategories,
			destinationCountry: Cart.destinationCountry ?? defaultDestinationCountry,
			mothersBlendProduct,
			cafeinaProduct,
			currency,
			vatType: Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
			exchangeRates: exchangeRatesSetting?.exchangeRates ?? [],
			countrySettings,
			mothersBlendConstantPriceEnabled: MOTHERS_BLEND_CONSTANT_PRICE_ENABLED,
		}),
	);
	const priceFrom = $derived(formatPrice(prices.min, currency, tenant.locale, false));
	const priceRange = $derived(formatPriceRange(prices.min, prices.max, currency, tenant.locale, false));
</script>

<Layout>
	<FathersClubHeroSection {priceFrom} />
	<FathersClubPricingSection {priceRange} />
	<FathersClubAdvantagesSection />
	<FathersClubHowItWorksSection />
	<FathersChoiceSection />
	<FathersClubFaqSection {priceFrom} />
	<ProductSection
		{subcategories}
		{products}
		title="[{getCategoryPath(tenant.locale, CategoryType.Coffee)}][[{translate('productsTitle')}]]"
		subtitle={translate("productsSubtitle")}
		class="bg-fcrbeige"
	/>
	<SubscriptionSection paragraph={translate("fathersFamilyParagraph")} />
	<SubscriptionKindsSection />
	<TaglineStrip />
	<ImageSection
		sources={{
			default: lastImageXs,
			">sm": lastImageSm,
			">lg": lastImageLg,
			">3xl": lastImage3xl,
		}}
		darkOverlay
		class={[lastImageHeight]}
	>
		{#snippet heading()}
			<Heading
				level={2}
				text={translate("clubTitle")}
				class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
			>
				<Media
					width={325}
					height={305}
					sourcesets={{}}
					source={{ type: "image/webp", src: clubLogo }}
					alt={translate("clubTitle")}
				/>
			</Heading>
		{/snippet}
		{#snippet actions()}
			<BuildYourSubscriptionButton variant={ButtonVariant.White} registerLabel />
		{/snippet}
	</ImageSection>
	<FloatingCta>
		<BuildYourSubscriptionButton class="w-full sm:w-fit" variant={ButtonVariant.Dark} registerLabel />
	</FloatingCta>
</Layout>
